import React from "react";
import styled from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import ColumnLayout from "../ColumnLayout";
import Links from "../../../Links";
import Essentials from "../../../../img/casestudy/design-systems/design-system-essentials-new.jpg";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import Img from "../../../../img/feelix.png";
import Image from "../../../Image";
import Box from "../../../Box";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";

const QuoteImage = styled.img`
  width: 100%;
  height: auto;
`;

const Quote = styled.div`
  background: #2f2f2f;
  border-radius: 22px;
  box-shadow: 0 34px 30px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const QuoteInner = styled.div`
  padding: var(--spacing-large);
  margin: 0;
  h4 {
    position: relative;
    line-height: 1.5;
    font-style: italic;
    font-family: serif;
    color: var(--white);
    margin-top: 0;
    &:before {
      content: open-quote;
      font-family: "Gentium Book Basic", serif;
      position: absolute;
      left: -3rem;
      font-size: 4rem;
      color: #5d5d5d;
      font-weight: bold;
      top: -1.5rem;
    }
  }
`;

const CaseStudyLinks = styled(Links)`
  color: white;
  border-color: rgba(255, 255, 255, 0.2);
  &:hover {
    color: #ff73df;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-large);
  @media (min-width: 700px) {
    gap: var(--spacing-large);
    grid-template-columns: 1fr 1fr;
  }
`;

const Feelix = (
  <CaseStudiesCard id="maturefeelix">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Transforming a widget library into a scalable design system for faster, more consistent product delivery at MYOB.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout wide>
      <Heading variant="larger">Maturing Feelix</Heading>

      <Stack spacing="var(--spacing-large)">
        <Grid>
          <Box>
            <Heading variant="small">The Beginnings</Heading>
            <p>
              In early 2018, MYOB launched a company-wide initiative to modernise its digital products and customer
              experiences. A key part of this transformation involved establishing a funded design system team focused
              on delivering tools, components, and guidelines to support consistent, high-quality experiences across
              product teams.
            </p>
          </Box>
          <Box>
            <Heading variant="small">The Opportunity</Heading>
            <p>
              I was part of the founding team behind the{" "}
              <Links href="https://feelix.myob.com/" target="_blank" rel="noopener noreferrer">
                Feelix Design System
              </Links>{" "}
              , helping shape the system’s vision, foundational principles, and implementation strategy. My work
              included early discovery efforts, setting a clear{" "}
              <Links href="/case-studies#roadmap" rel="noopener noreferrer">
                roadmap
              </Links>
              , defining the{" "}
              <Links href="/case-studies#contribution" rel="noopener noreferrer">
                contribution model
              </Links>
              , and guiding key decisions around whether to{" "}
              <Links href="/case-studies#buildvsbuy" rel="noopener noreferrer">
                build or buy components
              </Links>
              . I also led work on{" "}
              <Links href="/case-studies#designtokens" rel="noopener noreferrer">
                token structures
              </Links>{" "}
              , tooling and responsive strategy.
            </p>
          </Box>
        </Grid>

        <Image dark src={Img} alt="Feelix Source of Truth" caption="Source of Truth - feelix.myob.com" />

        <Box>
          <Heading variant="small">The Result</Heading>
          <Grid>
            <Box>
              <p>
                Feelix became the foundation for the rebuild of MYOB’s accounting software. It provided a complete set
                of UI building blocks used across the new platform, enabling teams to compose interfaces efficiently.
                The previous static Bootstrap interface was replaced with a fully responsive design that worked
                seamlessly across mobile, tablet, and desktop — and{" "}
                <Links href="#quote" rel="noopener noreferrer">
                  customers loved the experience
                </Links>
                .
              </p>
            </Box>
            <Box>
              <p>
                Internally, shifting to a mobile-first, device-agnostic architecture was a major change in how MYOB
                approached front-end development. My work supported this architectural vision, the creation of a single
                source of truth portal, and the broader guidelines that enabled the system to scale across teams.
              </p>
            </Box>
          </Grid>
        </Box>

        <Box>
          <ColumnLayout gridGap={{ lg: "var(--spacing-xx-large)" }} gridTemplateColumns={{ lg: "1fr 1.3fr" }}>
            <div>
              <Heading variant="small">My Role</Heading>
              <p>
                As a core contributor to the Feelix team, I played both strategic and hands-on roles — shaping the
                system from the ground up to ensure it could scale with MYOB’s evolving needs.
              </p>
              <p style={{ fontWeight: "bold" }}>My contributions included:</p>
              <ul>
                <li>Ensuring components follow atomic design principles</li>
                <li>Restructure the design system to use scalable, modular CSS</li>
                <li>Introduced responsive design across our components</li>
                <li>Embedding a baseline grid for vertical rhythm</li>
                <li>Defining a typographic scale with CSS</li>
                <li>Designed the design token structure and naming convention</li>
                <li>Leading the move to React layout components</li>
                <li>Designing Feelix page templates and layout components</li>
                <li>Expanding usage and technical guidelines</li>
                <li>Explored automating the UI-Kit to reflect the Design System</li>
              </ul>
            </div>
            <div>
              <Quote id="quote">
                <QuoteImage src={Essentials} alt="MYOB Incite: New Essentials using Feelix" height="236" />
                <QuoteInner>
                  <Heading variant="small">
                    Essentials is fully mobile responsive. Was very cool to see the desktop screen resize to a tablet
                    and then again to a mobile. Props, MYOB
                  </Heading>

                  <CaseStudyLinks
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://digitalfirst.com/myobs-new-ceo-is-building-a-new-myob-and-this-time-its-war/"
                  >
                    digitalfirst.com - Sholto Macpherson
                  </CaseStudyLinks>
                </QuoteInner>
              </Quote>
            </div>
          </ColumnLayout>
        </Box>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Feelix;
